import {
  ActionIcon as MantineActionIcon,
  ActionIconProps as MantineActionIconProps,
} from '@mantine/core';
import { ComponentPropsWithoutRef, forwardRef } from 'react';

import styles from './ActionIcon.module.css';

export type ActionIconSize = 'sm' | 'md' | 'lg' | 'xl';

interface BaseActionIconProps extends Omit<
  MantineActionIconProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size'
> {
  /** Size of the action icon. Default: 'lg' */
  size?: ActionIconSize;
  /** Adds data-checked attribute for selected/toggle state */
  isChecked?: boolean;
  /**
   * Renders the icon as a shadowed pill/circle for elevated/overlay contexts.
   * Default: false (non-floating = standard behavior).
   */
  isFloating?: boolean;
}

export type ActionIconProps = BaseActionIconProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof BaseActionIconProps | 'style'>;

export const ActionIcon = forwardRef<HTMLButtonElement, ActionIconProps>(
  (
    { size = 'lg', isChecked, isFloating, className, children, ...props },
    ref
  ) => {
    const rootClassName = [styles.root, className].filter(Boolean).join(' ');

    return (
      <MantineActionIcon
        ref={ref}
        unstyled
        classNames={{
          root: rootClassName,
          loader: styles.loader,
          icon: styles.icon,
        }}
        data-input-size={size}
        data-checked={isChecked || undefined}
        data-floating={isFloating || undefined}
        {...props}
      >
        {children}
      </MantineActionIcon>
    );
  }
);

ActionIcon.displayName = 'ActionIcon';
